import { Bell, LogOut, Menu, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/common';
import { ROUTES } from '@/config';
import { useAuth } from '@/hooks';
import { cn } from '@/lib';
import { useNotificationsStore, useUiStore } from '@/stores';

export function AdminHeader() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { unreadCount } = useNotificationsStore();
  const { sidebarOpen, setSidebarOpen } = useUiStore();

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const fullName = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : '';

  return (
    <header
      className={cn(
        'fixed top-0 right-0 z-20 flex h-16 items-center gap-4 border-b border-admin-border bg-admin-surface px-4 sm:px-6 transition-all duration-200',
        'left-0 lg:left-16',
        sidebarOpen && 'lg:left-60'
      )}
    >
      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        className="rounded-lg p-2 text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text lg:hidden"
        aria-label="Menu"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="flex-1" />

      {/* Notifications */}
      <button
        type="button"
        onClick={() => navigate(ROUTES.ADMIN_NOTIFICATIONS)}
        className="relative rounded-lg p-2 text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text"
        aria-label={t('nav.notifications')}
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-admin-surface-hover outline-none">
          <Avatar name={fullName} size="sm" />
          <span className="text-sm font-medium text-admin-text hidden sm:block">{fullName}</span>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate(ROUTES.ADMIN_PROFILE)}>
            <User className="h-4 w-4" />
            {t('nav.profile')}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="h-4 w-4" />
            {t('nav.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
